import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether the current filter draft differs from what was last actually
 * searched, so a Search button can surface a "you have unapplied changes"
 * cue instead of looking identical whether or not a click would change the
 * results.
 *
 * Filter bars mix two apply paths: the explicit Search button, and a few
 * actions (chip edit, chip remove, Clear All) that already re-run the search
 * immediately on click. `markApplied` must be called from every path that
 * calls `onSearch` — not just the Search button — or this hook would flag an
 * already-applied change as still pending.
 *
 * Filter state here is always plain data (strings, arrays, small nested
 * records — see FilterState / ShipmentFilterState / StructureFilterState), so
 * a JSON comparison is a reliable, cheap equality check. It would not be for
 * state containing functions, Dates, or Maps.
 */
export const useFilterDirty = <T>(filters: T) => {
  const lastAppliedRef = useRef<T>(filters);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setIsDirty(JSON.stringify(filters) !== JSON.stringify(lastAppliedRef.current));
  }, [filters]);

  const markApplied = useCallback((applied: T) => {
    lastAppliedRef.current = applied;
    setIsDirty(false);
  }, []);

  return { isDirty, markApplied };
};
